import { Anchor, Modal, Paper, Skeleton, Table, Text, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { KpiTicketLiegezeit, KpiTicketLiegezeitPerson, TicketZeitraum, Vorgangstyp } from "@msc/shared";

import { useKpiTicketLiegezeit, type TicketEingrenzung } from "../../../hooks/useKpiTickets.js";
import { formatBerlinDate } from "../../../lib/date.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";
import { arbeitstageText } from "./ticketDarstellung.js";

/** SR-01 als Tabelle, rein darstellend. */
export function LiegezeitTabelle({ daten, onPerson }: { daten: KpiTicketLiegezeit; onPerson: (konto: string) => void }) {
  const { t } = useTranslation();
  const einheit = t("pages.kpi.tickets.liegezeit.einheit");
  if (daten.personen.length === 0) return <Text size="sm" c="dimmed">{t("pages.kpi.tickets.liegezeit.leer")}</Text>;
  return (
    <Table.ScrollContainer minWidth={520}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.kpi.tickets.liegezeit.colPerson")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.liegezeit.colMittel")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.liegezeit.colOffen")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.liegezeit.colLaengste")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {daten.personen.map((p) => (
            <Table.Tr key={p.konto}>
              <Table.Td>
                <UnstyledButton aria-label={t("pages.kpi.tickets.liegezeit.knopfPerson", { name: p.name })} onClick={() => onPerson(p.konto)} c="var(--msc-navy-text)" fz="sm" td="underline">
                  {p.name}
                </UnstyledButton>
              </Table.Td>
              <Table.Td>
                {arbeitstageText(p.mittelTage, einheit)}
                {p.abgeschlossen > 0 && <Text span size="xs" c="dimmed"> {t("pages.kpi.tickets.liegezeit.ausN", { count: p.abgeschlossen })}</Text>}
              </Table.Td>
              <Table.Td>{p.offen}</Table.Td>
              <Table.Td>{arbeitstageText(p.laengsteTage, einheit)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function PersonModal({ person, jiraBaseUrl, onClose }: { person: KpiTicketLiegezeitPerson | null; jiraBaseUrl: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const einheit = t("pages.kpi.tickets.liegezeit.einheit");
  const basis = jiraBaseUrl?.replace(/\/+$/, "") ?? null;
  const tabelle = (tickets: KpiTicketLiegezeitPerson["offeneTickets"], spalteDatum: "seit" | "abschluss") => (
    <Table.ScrollContainer minWidth={560}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pages.teamDaily.kennzahlen.spalteTicket")}</Table.Th>
            <Table.Th>{t("pages.teamDaily.kennzahlen.spalteKunde")}</Table.Th>
            <Table.Th>{t("pages.kpi.tickets.liegezeit.colLiegezeit")}</Table.Th>
            <Table.Th>{t(spalteDatum === "seit" ? "pages.kpi.tickets.liegezeit.colSeit" : "pages.kpi.tickets.liegezeit.colAbschluss")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tickets.map((x) => (
            <Table.Tr key={`${x.key}-${x.seit}`}>
              <Table.Td>
                {basis ? <Anchor href={`${basis}/browse/${x.key}`} target="_blank" rel="noopener noreferrer">{x.key}</Anchor> : x.key}{" "}
                <Text span size="sm">{x.summary}</Text>
              </Table.Td>
              <Table.Td>{x.customerLabel ?? "–"}</Table.Td>
              <Table.Td>{arbeitstageText(x.liegezeitTage, einheit)}</Table.Td>
              <Table.Td>
                {spalteDatum === "seit" ? formatBerlinDate(x.seit) : x.abschluss ? `${formatBerlinDate(x.abschluss)} · ${t(`pages.kpi.tickets.liegezeit.art.${x.art ?? "abgegeben"}`)}` : "–"}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
  return (
    <Modal opened={person !== null} onClose={onClose} title={person?.name ?? ""} size="xl">
      {person && (
        <>
          <Text fw={700} size="sm" mb={4}>{t("pages.kpi.tickets.liegezeit.offeneTitel", { count: person.offen })}</Text>
          {person.offeneTickets.length > 0 ? tabelle(person.offeneTickets, "seit") : <Text size="sm" c="dimmed">{t("pages.teamDaily.kennzahlen.listeLeer")}</Text>}
          <Text fw={700} size="sm" mt="md" mb={4}>{t("pages.kpi.tickets.liegezeit.abgeschlosseneTitel", { count: person.abgeschlossen })}</Text>
          {person.abgeschlosseneTickets.length > 0 ? tabelle(person.abgeschlosseneTickets, "abschluss") : <Text size="sm" c="dimmed">{t("pages.teamDaily.kennzahlen.listeLeer")}</Text>}
        </>
      )}
    </Modal>
  );
}

/**
 * SR-01 „Welches Ticket liegt bei wem wie lange?" (Spec 2.1c). Momentaufnahme:
 * Vorgabe letzte 30 Tage. Liegezeit = zugewiesen und nicht wartend, in
 * Arbeitstagen. Die einzige Stelle der Bereichs-KPIs mit Bearbeiternamen
 * (`docs/data-classification.md`).
 */
export function LiegezeitBlock({ segmentId, eingrenzung }: { segmentId: string; eingrenzung: TicketEingrenzung }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [zeitraum, setZeitraum] = useState<TicketZeitraum | null>(null);
  const [konto, setKonto] = useState<string | null>(null);
  const { data, isLoading, isError } = useKpiTicketLiegezeit(segmentId, zeitraum, vorgangstyp, eingrenzung);
  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf
        titel={t("pages.kpi.tickets.liegezeit.titel")}
        spiegel={data?.spiegel}
        vorgangstyp={vorgangstyp}
        onVorgangstyp={setVorgangstyp}
        zeitraum={zeitraum}
        onZeitraum={setZeitraum}
        zeitraumVorgabe="letzte_30_tage"
        hinweis={t("pages.kpi.tickets.liegezeit.hinweis")}
      />
      {isLoading ? <Skeleton height={160} /> : isError || !data ? <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text> : <LiegezeitTabelle daten={data} onPerson={setKonto} />}
      <PersonModal person={data?.personen.find((p) => p.konto === konto) ?? null} jiraBaseUrl={data?.jiraBaseUrl ?? null} onClose={() => setKonto(null)} />
    </Paper>
  );
}
